import { GameFlowBase } from './game-flow-base';
import { cloneData, generateGameViewModel } from './game-view-data';
import { GameConfig } from './models/game-config.model';
import { CardModel, CardState, GameViewModel, HandModel, HandState, SideModel, SideState } from './models/game-view.model';
import { GameChangeEvent, TableUpdate } from '../models/communication/app-state.model';
import { CardValue } from '../models/communication/cards.enum';
import { CardPosition, HandValueKey, Outcome, SidePosition } from '../models/communication/games.model';

/**
 * An abstract class for all the main game types (`baccarat-game`, `dragon-tiger-game`…)
 *
 * The main game types extend the class with additional functionality
 * and provide `GameConfig` that defines `GameViewModel`.
 *
 * The property `flow` allows composing different game variants (flows) with the same sub classes,
 * (`open-game-flow`, `classic-game-flow`…)
 *
 * The class provides the API for manipulating view model data.
 * A set of high level helper methods define common actions needed at different game states.
 *
 * Finally, the abstract methods need to be implemented by the sub classes.
 */
export abstract class GameBase {
    view!: GameViewModel;
    readonly flow!: GameFlowBase<GameBase, GameChangeEvent>;

    protected constructor(config: GameConfig) {
        this.view = generateGameViewModel(config);
    }

    cloneData(): GameViewModel {
        return cloneData(this.view);
    }

    // Title

    updateTitle(text: string, params: { isSpecial: boolean } = { isSpecial: false }): void {
        this.view.title.text = text;
        this.view.title.isSpecial = params.isSpecial;
    }

    getTitleText(): string {
        return this.view.title.text;
    }

    // Cards

    getCardState(position: CardPosition): CardState {
        return this.getCard(position).state;
    }

    getCard(position: CardPosition): CardModel {
        return this.view.cards[position];
    }

    getCardParentHand(cardPosition: CardPosition): HandModel {
        return this.view.cards[cardPosition].parentHand;
    }

    getCardParentSide(cardPosition: CardPosition): SideModel {
        return this.view.cards[cardPosition].parentHand.parentSide;
    }

    showCard(position: CardPosition, value: CardValue): void {
        this.updateCard(position, CardState.Show, value);
    }

    updateCard(position: CardPosition, state: CardState, value: CardValue = CardValue.Unknown): void {
        const card = this.view.cards[position];
        card.state = state;
        card.value = value;
    }

    updateVisibleCards(state: CardState): void {
        for (const card of this.view.cards) {
            if (card.state !== CardState.Hidden) {
                this.updateCard(card.position, state);
            }
        }
    }

    // Sides

    getSideState(sidePosition: SidePosition): SideState {
        return this.getSide(sidePosition).state;
    }

    getSide(sidePosition: SidePosition): SideModel {
        const finalSide = this.view.sides.find((side) => side.position === sidePosition);
        if (!finalSide) {
            throw new Error('side must be defined!');
        }
        return finalSide;
    }

    focusOnCardParentSide(cardPosition: CardPosition): void {
        this.updateSides(SideState.Default);
        this.updateSide(this.getCardParentSide(cardPosition).position, SideState.Current);
    }

    updateSides(state: SideState): void {
        for (const side of this.view.sides) {
            this.updateSide(side.position, state);
        }
    }

    updateSide(sidePosition: SidePosition, state: SideState): void {
        for (const side of this.view.sides) {
            if (side.position === sidePosition) {
                side.state = state;
            }
        }
    }

    // Hands

    getFirstHandInSide(sidePosition: SidePosition): HandModel {
        return this.getHandInSide(this.getSide(sidePosition), 0);
    }

    getHandInSide(side: SideModel, handIndex = 0): HandModel {
        return side.hands[handIndex];
    }

    updateHands(state: HandState): void {
        for (const hand of this.view.hands) {
            this.updateHand(hand, state);
        }
    }

    updateHand(hand: HandModel, state: HandState): void {
        // eslint-disable-next-line no-param-reassign
        hand.state = state;
    }

    getCardInHand(hand: HandModel, cardIndex = 0): CardModel {
        return hand.cards[cardIndex];
    }

    // Helper methods

    revealCard(cardPosition: CardPosition, card: CardValue): void {
        this.showCard(cardPosition, card);
        const wordCard = this.getWordCard({ lowerCase: false, plural: this.getCardParentHand(cardPosition).cards.length > 1 });
        this.updateTitle($localize`${wordCard} flipped`, { isSpecial: true });
        this.focusOnCardParentSide(cardPosition);
    }

    getWordCard(params: { plural: boolean; lowerCase: boolean } = { plural: false, lowerCase: true }): string {
        let word = params.plural ? $localize`Cards` : $localize`Card`;
        if (params.lowerCase) {
            word = word.toLocaleLowerCase();
        }
        return word;
    }

    showHandValue(table: TableUpdate): void {
        const hand = this.getCardParentHand(table.cardPosition);

        hand.amount.visible = true;

        if (hand.handValueKey !== undefined) {
            hand.amount.value = (table[hand.handValueKey as keyof TableUpdate] as number) || 0;
        }
    }

    updateHandValues(handValueMap: Partial<Record<HandValueKey, number>>): void {
        for (const hand of this.view.hands) {
            if (hand.handValueKey) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                hand.amount.value = handValueMap[hand.handValueKey]!;
            }
        }
    }

    showFaceUpMessage(cardPosition: CardPosition): void {
        this.updateCard(cardPosition, CardState.MessageFaceUp);
        this.updateTitle($localize`Draw face up card and place card on ${this.getFieldLabel(cardPosition)}`);
        this.focusOnCardParentSide(cardPosition);
    }

    showFaceDownMessage(cardPosition: CardPosition): void {
        this.updateCard(cardPosition, CardState.MessageFaceDown);
        this.updateTitle($localize`Draw face down card and place card on ${this.getFieldLabel(cardPosition)}`);
        this.focusOnCardParentSide(cardPosition);
    }

    showPlayerDecisionTime(): void {
        this.updateTitle($localize`Player decision time`, { isSpecial: true });
    }

    showAllCardsDrawn(): void {
        this.updateTitle($localize`All cards have been drawn`, { isSpecial: true });
        this.updateSides(SideState.Current);
    }

    // Outcome

    handleOutcome(table: Pick<TableUpdate, 'outcome' | 'outcomes'>): void {
        if (table.outcome !== undefined) {
            this.updateTitle(this.getOutcomeLabel(table.outcome));
            this.updateSides(SideState.Default);
            this.setOutcomeStates(table.outcome, table.outcomes);
        }
    }

    // Shrink mode

    onEnterShrinkMode(): void {
        this.view.shrinkMode = true;
    }

    onExitShrinkMode(): void {
        this.view.shrinkMode = false;
    }

    // Abstract methods

    abstract setOutcomeStates(outcome: Outcome, outcomes?: Outcome[]): void;
    abstract getFirstCardPosition(): CardPosition;
    abstract getFieldLabel(cardPosition: CardPosition): string;
    abstract getOutcomeLabel(outcome: Outcome): string;
}
